/**
 * Words and labels for the buckets of the statistics.
 *
 * Every bucket is a utc hour, day or month, and so is every word here: the formatting is told
 * timeZone "UTC" each time, or a bucket that starts at 00:00 utc would be labelled with the
 * evening before for anybody west of Greenwich. The screen says the times are utc.
 */

import { formatCount } from "./chart";
import { t } from "../i18n/strings";
import type { StatsBucket, UsersStats } from "../api/types";

type Unit = UsersStats["unit"];

const UTC = { timeZone: "UTC" } as const;

function getHour(date: Date): string {
  return `${String(date.getUTCHours()).padStart(2, "0")}:00`;
}

/**
 * Three letters of the month, the way an axis twelve labels wide needs them.
 *
 * The first three letters of the full name and not the locale's short form: that one is
 * "сент." and "нояб." next to "март" and "май", and does not fit the slot of one bar.
 */
function getMonthShort(date: Date): string {
  return date.toLocaleString("ru-RU", { ...UTC, month: "long" }).slice(0, 3);
}

function capitalize(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

/**
 * What the axis says under one bar, or null where it says nothing.
 *
 * Hours are labelled on the clock - 00:00, 04:00, 08:00 - whatever the window starts with.
 * Days are counted back from today, so the last bar, the one being watched, always has its
 * date. Months have room for a label each.
 */
export function getTickLabel(unit: Unit, buckets: StatsBucket[], index: number): string | null {
  const start = new Date(buckets[index]!.start);

  if (unit === "hour") {
    return start.getUTCHours() % 4 === 0 ? getHour(start) : null;
  }
  if (unit === "day") {
    return (buckets.length - 1 - index) % 5 === 0
      ? start.toLocaleString("ru-RU", { ...UTC, day: "2-digit", month: "2-digit" })
      : null;
  }
  return getMonthShort(start);
}

/**
 * The interval one bar covers, in words: "17 октября, 14:00–15:00", "17 октября 2026 г.",
 * "Октябрь 2026 г.".
 *
 * The current bucket is not over, so an hour of it ends at the moment it was counted and not
 * at the next hour - "15:00–15:40" says how much of it the number covers.
 */
export function getBucketTitle(unit: Unit, bucket: StatsBucket, now: string | null): string {
  const start = new Date(bucket.start);

  if (unit === "hour") {
    const to = now
      ? new Date(now).toLocaleString("ru-RU", { ...UTC, hour: "2-digit", minute: "2-digit" })
      : getHour(new Date(bucket.end));
    return t("stats.hourRange", {
      day: start.toLocaleString("ru-RU", { ...UTC, day: "numeric", month: "long" }),
      from: getHour(start),
      to,
    });
  }
  if (unit === "day") {
    return start.toLocaleString("ru-RU", { ...UTC, day: "numeric", month: "long", year: "numeric" });
  }
  return capitalize(start.toLocaleString("ru-RU", { ...UTC, month: "long", year: "numeric" }));
}

/** The bucket a change is counted against, in words: "днём раньше". */
export function getPreviousLabel(unit: Unit): string {
  if (unit === "hour") {
    return t("stats.previous.hour");
  }
  if (unit === "day") {
    return t("stats.previous.day");
  }
  return t("stats.previous.month");
}

/** A change with its sign written out: "+2", "−3", and a plain "0" when nothing changed. */
export function formatChange(value: number): string {
  return value > 0 ? `+${formatCount(value)}` : formatCount(value);
}

/** The line a popover adds under the current bucket, which is still filling up. */
export function getCurrentNote(unit: Unit): string {
  if (unit === "hour") {
    return t("stats.current.hour");
  }
  if (unit === "day") {
    return t("stats.current.day");
  }
  return t("stats.current.month");
}
